import { FC, useMemo, useState } from 'react';
import { AddressType, AvaSuburbs } from '@models';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  debounce,
} from '@mui/material';
import { Box } from '@mui/system';
import { Control, Controller, FieldErrors, UseFormHandleSubmit, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { IState, AU_STATES, postcodeError, AddressFormData } from '@constants';

interface Props {
  availableSuburbs: AvaSuburbs[];
  control: Control<AddressFormData, any>;
  errors: FieldErrors<AddressFormData>;
  handleSubmit: UseFormHandleSubmit<AddressFormData>;
  isLoading: boolean;
  onSubmit: (data: AddressFormData) => void;
  register: UseFormRegister<AddressFormData>;
  setStateProvince?: (state: IState) => void;
  setValue: UseFormSetValue<AddressFormData>;
}

export const AddNewAddressForm: FC<Props> = ({
  availableSuburbs,
  control,
  errors,
  handleSubmit,
  isLoading,
  onSubmit,
  register,
  setStateProvince,
  setValue,
}) => {
  const [addressType, setAddressType] = useState('residential' as AddressType);
  const [isValidPostcode, setIsValidPostcode] = useState(true);

  const handlePostcodeChangeDebounced = useMemo(
    () => debounce((p: string) => setIsValidPostcode(!!availableSuburbs?.find((postcode) => postcode.suburb_postcode === p)), 300),
    [availableSuburbs],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid="address-form" id="manual-address-form">
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <TextField
            {...register('address2')}
            fullWidth
            label="Subdwelling"
            name="address2"
            InputLabelProps={{ shrink: true }}
            data-testid="sub-dwelling-address"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            {...register('address1')}
            fullWidth
            error={!!errors.address1}
            helperText={errors.address1?.message}
            label="Street Address *"
            name="address1"
            InputLabelProps={{ shrink: true }}
            data-testid="street-address"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField {...register('company')} fullWidth label="Company Name" data-testid="company" />
        </Grid>
        <Grid item xs={4}>
          <TextField
            {...register('city')}
            fullWidth
            error={!!errors.city}
            helperText={errors.city?.message}
            label="City or Suburb *"
            name="city"
            InputLabelProps={{ shrink: true }}
            data-testid="suburb"
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            {...register('postal_code')}
            fullWidth
            error={!!errors.postal_code || !isValidPostcode}
            helperText={!isValidPostcode ? postcodeError : errors.postal_code?.message}
            label="Postcode *"
            data-testid="postal-code"
            name="postal_code"
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handlePostcodeChangeDebounced(e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel id="state-label">State *</InputLabel>
            <Controller
              control={control}
              name="state_or_province"
              render={({ field }) => (
                <Select
                  error={!!errors.state_or_province}
                  id="state-select"
                  label="State"
                  labelId="state-label"
                  {...register('state_or_province')}
                  data-testid="state-select"
                  role="listbox"
                  {...field}
                  onChange={(event, value) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const selectedObj: IState = AU_STATES.find((item) => item.id === event.target.value)!;

                    field.onChange(value);
                    setStateProvince && setStateProvince(selectedObj);
                    setValue('state_or_province', selectedObj.id);
                  }}
                >
                  {AU_STATES.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.value}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.state_or_province && <FormHelperText>{errors.state_or_province.message}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            {...register('latitude')}
            error={!!errors.latitude}
            helperText={errors.latitude?.message}
            label="Latitude *"
            name="latitude"
            data-testid="latitude"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            {...register('longitude')}
            fullWidth
            error={!!errors.longitude}
            helperText={errors.longitude?.message}
            label="Longitude *"
            name="longitude"
            data-testid="longitude"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={4}>
          <FormControl component="fieldset" error={!!errors.address_type}>
            <FormLabel component="legend">Address Type *</FormLabel>
            <Controller
              control={control}
              name="address_type"
              render={({ field }) => (
                <RadioGroup
                  row
                  aria-label="address type"
                  {...field}
                  value={addressType}
                  onChange={(e, type) => {
                    field.onChange(type);
                    setAddressType(type as AddressType);
                  }}
                >
                  <FormControlLabel control={<Radio />} label="Residential" value="residential" data-testid="radio-button-residential" />
                  <FormControlLabel control={<Radio />} label="Commercial" value="commercial" data-testid="radio-button-commercial" />
                </RadioGroup>
              )}
            />
            {errors.address_type && <FormHelperText>{errors.address_type.message}</FormHelperText>}
          </FormControl>
        </Grid>
      </Grid>
      <Box marginTop={2} display="flex" justifyContent="flex-end">
        <LoadingButton loading={isLoading} variant="contained" type="submit" data-testid="submit-button" disabled={!isValidPostcode}>
          Save
        </LoadingButton>
      </Box>
    </form>
  );
};
